import React from 'react';
import { useParams, useLocation, Link, Navigate } from 'react-router-dom';
import { resolveDmaName } from '../../components/self-service/targetingRow';
import {
  Loader2, ArrowLeft, Building2, Globe, Mail, User, Phone,
  Calendar, Tv, MapPin, Film, ExternalLink, AlertCircle, Radio, XCircle, Download,
  BarChart3, ArrowRight, ChevronDown, CreditCard,
} from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import SignInModal from '../../components/self-service/auth/SignInModal';
import { formatMoneySmart } from '../../components/self-service/shared/format';
import { usePublisherConfig } from '../../data/publisherConfigs';
import CampaignStatusDashboard from '../../components/self-service/CampaignStatusDashboard';
import OrderReceipt from '../../components/self-service/OrderReceipt';
import type { ReceiptExtensionCharge, ReceiptPaymentStatus } from '../../components/self-service/receiptPayments';
import { exportReceiptToPdf, receiptFilenameFor } from '../../services/orderReceiptPdf';
import { apiClient } from '../../lib/apiClient';
import type { SelfServiceCreativeRow, SelfServiceSubmissionRow } from '../../types/selfService';
import { formatDate, formatDateTime, formatShortRange } from '../../components/self-service/shared/dateFormat';
import {
  AdPlayProgress, Stat, SharedKeyframes,
  bucketBadge, bucketFor, bookedPlays, campaignDelivery, campaignDates, statusToStep, approvedReviewGate, canEditInModal, hasDeliveredData,
} from '../../components/self-service/campaignSharedHelpers';
import { REJECTION_REASONS_BY_CODE } from '../../components/self-service/rejectionReasons';
import PublicHeader from '../../components/self-service/PublicHeader';
import { REPORT_GRADIENT_FROM, REPORT_GRADIENT_TO } from '../../components/self-service/reportTheme';
import { RerunMenu, FixAndResubmitButton, FinishCheckoutButton, CancelButton, EditButton, EditPreApprovalButton } from '../../components/self-service/campaignActions';
import { EditCampaignModal } from '../../components/self-service/EditCampaignModal';
import { canCancel, applyOptimisticBlanketExtension } from '../../components/self-service/campaignSharedHelpers';
import { isIncompleteCheckout } from '../../components/self-service/shared/checkoutStatus';
import CreativeScheduleChart from '../../components/self-service/scheduler/CreativeScheduleChart';

// CTV-611 / ADR-0023: the delivery Gantt charts delivery history and the
// scheduled future only: active, pending_review, and retired. Voided rows
// never delivered and never will, so they never chart (they surface as
// rejection cards instead); withdrawn/abandoned are filtered server-side.
// Local-time YYYY-MM-DD, matching the page's other date-only comparisons.
const localTodayYmd = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const chartCreatives = (creatives: SelfServiceCreativeRow[] | undefined) =>
  (creatives ?? []).filter(
    (c) => c.status === 'active' || c.status === 'pending_review' || c.status === 'retired',
  );
import {
  computeRotationSegments,
  formatSegmentSharesSummary,
} from '../../components/self-service/shared/rotationSegments';
import { validateCreativeCoverage } from '../../components/self-service/shared/creativeCoverage';
import { buildStationPalette } from '../../lib/dateAxisRendering';
import StripePaymentSection, { StripePaymentActionSection } from '../../components/self-service/StripePaymentSection';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import Banner from '../../components/self-service/shared/Banner';
import ScheduledStartBanner from '../../components/self-service/shared/ScheduledStartBanner';

type SubmissionRow = SelfServiceSubmissionRow;

// CTV-440 + CTV-516: shape returned by GET /submissions/:id/edits. One row per
// audited Submission Edit. Extensions feed the receipt's upfront payment rows;
// extensions + creative changes feed the Campaign edits history card below.
interface EditCreativeRef {
  id: string | null;
  filename: string;
}

interface EditCreativeChange {
  added: EditCreativeRef[];
  removed: EditCreativeRef[];
  updated: Array<{ id: string; filename: string; startDate: string | null; endDate: string | null }>;
}

interface EditRow {
  id: string;
  edit_kind: 'extend' | 'creative_swap' | 'creative_set_update';
  status: 'pending_review' | 'ready' | 'applied' | 'failed' | 'voided';
  previous_end_date: string | null;
  new_end_date: string | null;
  additional_budget_cents: number | null;
  // CTV-518: actual total charged for this extension (budget + service fee),
  // when the backend exposes it. Preferred over the local estimate below.
  amount_cents?: number | null;
  // CTV-504: null when the extension went through the no-charge path (monthly
  // intra-bucket bump of an unpaid tail installment). Distinguishes an
  // extension that charged the card from one that only bumped an installment.
  payment_intent_id: string | null;
  // CTV-516: resolved old/new creative filenames for creative_swap /
  // creative_set_update edits; null for extends.
  creative_change: EditCreativeChange | null;
  created_at: string;
  applied_at: string | null;
}

interface PaymentRecoverySetupResponse {
  clientSecret: string;
  setupIntentId: string;
  amountCents: number;
  paymentPlan: 'upfront' | 'monthly';
}

interface PaymentRecoveryRetryResponse {
  status: 'requires_action' | 'processing' | 'payment_authorized' | 'approved';
  clientSecret?: string;
  paymentIntentId?: string;
  row?: SubmissionRow;
}

const AdvertiserCampaignDetailPage: React.FC = () => {
  const { slug, submissionId } = useParams<{ slug: string; submissionId: string }>();
  const { user, isAuthenticated, selectedAdvertiser } = useAuth();
  const [signInOpen, setSignInOpen] = React.useState(!isAuthenticated);
  const [row, setRow] = React.useState<SubmissionRow | null>(null);
  const [edits, setEdits] = React.useState<EditRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState<string | null>(null);
  const [recoveryClientSecret, setRecoveryClientSecret] = React.useState<string | null>(null);
  const [recoverySetupIntentId, setRecoverySetupIntentId] = React.useState<string | null>(null);
  const [recoveryAction, setRecoveryAction] = React.useState<{ clientSecret: string; paymentIntentId: string } | null>(null);
  const [recoveryBusy, setRecoveryBusy] = React.useState(false);
  const [recoveryError, setRecoveryError] = React.useState<string | null>(null);
  const [recoverySuccess, setRecoverySuccess] = React.useState<string | null>(null);

  React.useEffect(() => { setSignInOpen(!isAuthenticated); }, [isAuthenticated]);

  // CTV-468 retention: campaign detail revisits. (delivery_report_viewed now
  // fires on the dedicated report page — CTV-445.)
  React.useEffect(() => {
    if (submissionId) {
      analytics.track(EVENTS.CAMPAIGN_DETAIL_VIEWED, {
        submission_id: submissionId,
        station_slug: slug,
      });
    }
  }, [submissionId, slug]);

  React.useEffect(() => {
    if (row?.status === 'payment_failed' && row.payment_plan === 'upfront') return;
    setRecoveryClientSecret(null);
    setRecoverySetupIntentId(null);
    setRecoveryAction(null);
    setRecoveryError(null);
    setRecoverySuccess(null);
  }, [row?.payment_plan, row?.status]);

  // Seat redesign: campaigns belong to the advertiser, not to the user's
  // email. Fetch the full list for the currently-selected advertiser and find
  // the requested row. Membership is verified server-side.
  // `silent` mode (used by the focus listener) skips the loading-state flip
  // and keeps the stale row on screen until the fetch returns — otherwise
  // refocusing the tab visibly remounts the whole page (loading branch swap,
  // CampaignDeliveryReport refetches, scroll resets, preview blinks).
  const fetchRow = React.useCallback(async (advertiserId: string, id: string, silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      // Closed-loop scoping: the URL pins the station, so the request asks
      // the API to filter the list down to one station before we pick the row.
      const { rows } = await apiClient.post<{ rows: SubmissionRow[] }>(
        '/submissions/list-by-advertiser',
        { advertiserId, stationSlug: slug },
      );
      const found = (rows ?? []).find((r) => r.id === id) ?? null;
      setRow(found);
      if (silent) setError(null);
    } catch (e) {
      if (!silent) {
        setError(e instanceof Error ? e.message : 'Failed to load campaign');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [slug]);

  React.useEffect(() => {
    if (selectedAdvertiser?.advertiserId && submissionId) {
      fetchRow(selectedAdvertiser.advertiserId, submissionId);
    }
  }, [selectedAdvertiser?.advertiserId, submissionId, fetchRow]);

  // CTV-637: when the buyer opens a stranded checkout, reconcile it against
  // Stripe once. If the card actually authorized (we missed the handshake), the
  // backend recovers it into a real campaign and we silently refetch so the page
  // renders as paid instead of offering discard/retry on a payment that already
  // went through. Best-effort: a verify failure leaves the pending_payment view
  // as-is, and FinishCheckoutButton re-verifies before any re-charge.
  const verifiedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const advId = selectedAdvertiser?.advertiserId;
    if (!advId || !submissionId) return;
    if (row?.status !== 'pending_payment') return;
    if (verifiedRef.current === submissionId) return;
    verifiedRef.current = submissionId;
    apiClient
      .post<{ status: string; recovered: boolean }>(`/submissions/${submissionId}/verify-checkout`, {})
      .then((res) => {
        if (res.recovered) fetchRow(advId, submissionId, true);
      })
      .catch(() => {
        // Leave the pending_payment view in place; the destructive actions
        // re-verify server-side before doing anything irreversible.
      });
  }, [row?.status, submissionId, selectedAdvertiser?.advertiserId, fetchRow]);

  // CTV-440: fetch the per-submission edit log to power the "Charges" section
  // below. Failures are non-fatal — the page still renders the original
  // receipt; we just lose the extensions list.
  const fetchEdits = React.useCallback(async () => {
    if (!submissionId) return;
    try {
      const res = await apiClient.get<{ rows: EditRow[] }>(`/submissions/${submissionId}/edits`);
      setEdits(res.rows ?? []);
    } catch {
      // Non-fatal — Charges section just won't update.
    }
  }, [submissionId]);

  React.useEffect(() => {
    void fetchEdits();
  }, [fetchEdits, row?.id]);

  // CTV-658: the page-level signed-URL fetch is gone with the legacy
  // single-commercial layout — every creative renders as a MultiCreativeCard,
  // which fetches its own per-creative view URL.

  React.useEffect(() => {
    const advertiserId = selectedAdvertiser?.advertiserId;
    if (!advertiserId || !submissionId) return;
    const bump = () => fetchRow(advertiserId, submissionId, true);
    window.addEventListener('focus', bump);
    return () => window.removeEventListener('focus', bump);
  }, [selectedAdvertiser?.advertiserId, submissionId, fetchRow]);

  // Hooks must run unconditionally — pull this above the early returns.
  // CTV: drive the publisher config off the URL slug (known on mount) rather
  // than waiting for `row.publisher_slug` to arrive. The two are equivalent —
  // `fetchRow` already constrains the submission list to `stationSlug: slug` —
  // but using the URL value lets the config fetch run in parallel with the
  // submission fetch, eliminating the cold-load styling flash where the page
  // chrome renders unstyled until the row resolves.
  const { config: cfg, loading: cfgLoading } = usePublisherConfig(slug);

  // CTV-516: upfront extension charges fed into the receipt as discrete
  // payment rows. Each non-voided `extend` edit on an upfront campaign is a
  // real Stripe capture (media + service fee). Monthly extensions instead
  // bump the live installment schedule, which the receipt already reflects, so
  // they're not surfaced here. Computed via useMemo so it runs unconditionally
  // — keep above the early returns alongside the other Hooks.
  const extensionCharges = React.useMemo<ReceiptExtensionCharge[]>(() => {
    if (!row || !cfg || row.payment_plan !== 'upfront') return [];
    const out: ReceiptExtensionCharge[] = [];
    for (const e of edits) {
      if (e.edit_kind !== 'extend') continue;
      if (e.status === 'voided') continue;
      const media = e.additional_budget_cents ?? 0;
      // Prefer the backend's actual charged cents when the edits payload
      // carries them; otherwise the added budget itself (CTV-635: the
      // extension charge is the added budget exactly). Backend cents are
      // authoritative whenever present.
      const amountCents = e.amount_cents ?? media;
      const status: ReceiptPaymentStatus =
        e.status === 'failed' ? 'failed' : e.payment_intent_id != null ? 'paid' : 'pending';
      out.push({
        id: e.id,
        amountCents,
        // Successful capture date when available, else the edit's creation date.
        date: e.applied_at ?? e.created_at,
        status,
      });
    }
    return out;
  }, [row, cfg, edits]);

  // CTV-611: the Edit-campaign modal opens from two places: the actions-strip
  // EditButton (which owns its own instance) and the per-creative
  // "Fix & resubmit" CTA on an unresolved rejected commercial, which opens a
  // page-level instance preselected on the Edit commercial tab. Both share
  // the callbacks below.
  const [fixModalOpen, setFixModalOpen] = React.useState(false);
  // CTV-611: completed runs collapse behind a "Ran previously" expander.
  const [showPastCreatives, setShowPastCreatives] = React.useState(false);

  // CTV-658: #fix-creatives deep link (My Campaigns card "Fix & resubmit").
  // Once the row is loaded, open the Edit-campaign modal preselected on the
  // Edit commercial tab so the buyer lands directly in the fix flow instead
  // of having to find it on the page. Fires once (ref-guarded) and strips
  // the hash so refresh/back doesn't re-open the modal.
  const location = useLocation();
  const fixLinkConsumedRef = React.useRef(false);
  React.useEffect(() => {
    if (location.hash !== '#fix-creatives') return;
    if (fixLinkConsumedRef.current) return;
    if (!row || !cfg) return;
    fixLinkConsumedRef.current = true;
    const hasUnresolvedRejection = (row.creatives ?? []).some(
      (c) => c.status === 'voided' && c.resolved_at == null,
    );
    if (row.status === 'approved' && hasUnresolvedRejection) {
      // Parity with the on-page Fix & resubmit CTA's open tracking.
      analytics.track(EVENTS.EDIT_CAMPAIGN_OPENED, {
        station_slug: slug,
        submission_id: row.id,
        tab: 'swap',
      });
      setFixModalOpen(true);
    }
    window.history.replaceState(null, '', location.pathname + location.search);
  }, [location.hash, location.pathname, location.search, row, cfg, slug]);

  const handleExtended = React.useCallback(
    (_id: string, newEndDate: string, addBudget: number) => {
      // Optimistic bump so the dates/budget update instantly while
      // the round-trip is in flight.
      setRow((prev) => {
        if (!prev) return prev;
        const previousEndDate = prev.campaign?.endDate;
        const withBumpedCreatives = previousEndDate
          ? applyOptimisticBlanketExtension(prev, previousEndDate, newEndDate)
          : prev;
        return {
          ...withBumpedCreatives,
          campaign: {
            ...withBumpedCreatives.campaign,
            endDate: newEndDate,
            ...(addBudget > 0 && withBumpedCreatives.campaign?.budget != null
              ? { budget: withBumpedCreatives.campaign.budget + addBudget }
              : {}),
          },
        };
      });
      // CTV-504: pull the canonical row + edits so the receipt
      // schedule (live installments) and the Charges list
      // reflect the just-applied extension. The optimistic
      // update above only patches dates/budget, the bumped
      // tail installment and the new edit row only come from
      // the server.
      if (selectedAdvertiser?.advertiserId && submissionId) {
        void fetchRow(selectedAdvertiser.advertiserId, submissionId, true);
        void fetchEdits();
      }
    },
    [selectedAdvertiser?.advertiserId, submissionId, fetchRow, fetchEdits],
  );

  const handleCreativeSwapped = React.useCallback(() => {
    if (selectedAdvertiser?.advertiserId && submissionId) {
      void fetchRow(selectedAdvertiser.advertiserId, submissionId, true);
      // CTV-516: refresh the edit log too so the Campaign
      // edits card shows the just-submitted creative
      // change without a page reload (mirrors onExtended).
      void fetchEdits();
    }
  }, [selectedAdvertiser?.advertiserId, submissionId, fetchRow, fetchEdits]);

  const handleDownloadReceipt = async () => {
    if (!row || !cfg) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      await exportReceiptToPdf({ row, config: cfg, extensionCharges }, receiptFilenameFor(row.id, row.created_at));
      analytics.track(EVENTS.RECEIPT_DOWNLOADED, { station_slug: slug, submission_id: row.id });
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  const startPaymentRecovery = async () => {
    if (!row || row.payment_plan !== 'upfront') return;
    setRecoveryBusy(true);
    setRecoveryError(null);
    setRecoverySuccess(null);
    setRecoveryAction(null);
    try {
      const data = await apiClient.post<PaymentRecoverySetupResponse>(
        `/submissions/${row.id}/payment-recovery/setup-intent`,
        {},
      );
      setRecoveryClientSecret(data.clientSecret);
      setRecoverySetupIntentId(data.setupIntentId);
      analytics.track(EVENTS.PAYMENT_RECOVERY_STARTED, { station_slug: slug, submission_id: row.id });
    } catch (e) {
      setRecoveryError(e instanceof Error ? e.message : 'Unable to start payment recovery');
    } finally {
      setRecoveryBusy(false);
    }
  };

  const handleRecoveryResult = (data: PaymentRecoveryRetryResponse) => {
    if (data.status === 'requires_action') {
      if (!data.clientSecret || !data.paymentIntentId) {
        setRecoveryError('Stripe requires bank verification but did not return the verification details.');
        return;
      }
      setRecoveryAction({ clientSecret: data.clientSecret, paymentIntentId: data.paymentIntentId });
      setRecoverySuccess(null);
      return;
    }
    if (data.row) setRow(data.row);
    setRecoveryClientSecret(null);
    setRecoverySetupIntentId(null);
    setRecoveryAction(null);
    analytics.track(EVENTS.PAYMENT_RECOVERED, {
      station_slug: slug,
      submission_id: row?.id,
      status: data.status,
    });
    setRecoverySuccess(
      data.status === 'processing'
        ? 'Your payment is processing. This page will update once Stripe confirms it.'
        : 'Payment recovered. Your campaign is back in review.',
    );
  };

  const retryPaymentRecovery = async (paymentIntentId?: string | null) => {
    if (!row || !recoverySetupIntentId || row.payment_plan !== 'upfront') return;
    setRecoveryBusy(true);
    setRecoveryError(null);
    try {
      const data = await apiClient.post<PaymentRecoveryRetryResponse>(
        `/submissions/${row.id}/payment-recovery/retry`,
        {
          setupIntentId: recoverySetupIntentId,
          ...(paymentIntentId ? { paymentIntentId } : {}),
        },
      );
      handleRecoveryResult(data);
    } catch (e) {
      setRecoveryError(e instanceof Error ? e.message : 'Unable to retry payment');
    } finally {
      setRecoveryBusy(false);
    }
  };

  if (!submissionId) return <Navigate to={`/${slug}/my-campaigns`} replace />;

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Sign in to view this campaign</h1>
          <button
            type="button"
            onClick={() => setSignInOpen(true)}
            className="mt-4 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
          >
            Sign in
          </button>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  // Hold the page chrome until the publisher config is in hand so the header,
  // logo, and accent colors render correctly on first paint. Warm navigations
  // from My Campaigns hit the synchronous cache seed in usePublisherConfig
  // and skip this gate entirely.
  if (!cfg && cfgLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const publisherName = cfg?.name ?? row?.publisher_slug ?? '';
  const cardLabel = row?.campaign?.campaignName?.trim() || row?.business_info?.businessName || '—';
  const cpmValue = row?.line_item_cpm != null ? `$${row.line_item_cpm.toFixed(2)}` : '—';
  const isSingleMarket = (cfg?.markets?.length ?? 0) === 1;
  const targetingMode = (row?.campaign?.targetingMode ?? 'default') as 'default' | 'custom';
  const targetingValue = isSingleMarket
    ? (targetingMode === 'default'
        ? (cfg?.markets?.[0]?.channels ?? []).join(', ') || cfg?.name || '—'
        : 'Custom geotargeting')
    : ((row?.campaign?.targetMarkets ?? [])
        .map((code) => resolveDmaName(cfg, code))
        .join(', ') || '—');
  const targetingLabel = isSingleMarket
    ? (targetingMode === 'default' ? 'Channel' : 'Targeting')
    : 'Markets';
  const budgetStr = row?.campaign?.budget != null ? `$${formatMoneySmart(row.campaign.budget)}` : '—';

  const isRejected = row?.status === 'creative_rejected';
  const isCanceled = row?.status === 'canceled';
  // CTV-637: a stranded checkout (never paid). Suppress every paid-campaign
  // affordance below — the delivery tracker, the fake "0 of N" booked bar, the
  // receipt, the PDF — and show the honest "payment not completed" state with
  // finish/discard actions instead.
  const isIncomplete = isIncompleteCheckout(row?.status);
  const badge = row ? bucketBadge(row) : null;
  const isLive = row ? bucketFor(row) === 'active' : false;
  const accent = cfg?.accentColor || cfg?.primaryColor;

  return (
    <div className="min-h-screen bg-slate-50">
      <SharedKeyframes />
      <PublicHeader
        publisher={cfg ?? undefined}
        rightSlot={
          <Link
            to={`/${slug}/my-campaigns`}
            className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            My Campaigns
          </Link>
        }
      />

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6">
        {loading ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading campaign…
          </div>
        ) : error ? (
          <Banner tone="warn" label="Couldn't load this campaign">{error}</Banner>
        ) : !row ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <AlertCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700 font-medium">Campaign not found</p>
            <p className="text-sm text-slate-500 mt-1">It may have been removed, or you may be signed in with a different email.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Hero card — same visual language as the My Campaigns card */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 mb-4 pt-1">
                <span aria-hidden />
                <div className="text-lg md:text-xl font-semibold tracking-tight text-slate-900 text-center">
                  {cardLabel}
                </div>
                <div className="flex items-center justify-end gap-2">
                  {cfg?.logoUrl && (
                    <img
                      src={cfg.logoUrl}
                      alt={publisherName}
                      className={`h-5 w-auto max-w-[72px] object-contain ${cfg.logoInvert === true ? 'brightness-0 invert' : ''}`}
                    />
                  )}
                  {badge && (
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded shrink-0 ${badge.className} ${isLive ? 'live-pulse' : ''}`}
                    >
                      {badge.label}
                    </span>
                  )}
                </div>
              </div>

              {/* Stat row — same Stat component as cards */}
              {(() => {
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 -mx-4">
                    <Stat label="Budget" value={budgetStr} accentColor={accent} />
                    <Stat label="Dates" value={formatShortRange(row.campaign?.startDate, row.campaign?.endDate)} accentColor={accent} showRail />
                    <Stat label="CPM" value={cpmValue} accentColor={accent} showRail hint="Cost per 1,000 streaming ad plays." />
                    <Stat label={targetingLabel} value={targetingValue} accentColor={accent} showRail />
                  </div>
                );
              })()}

              {/* CTV-637: a stranded checkout is not a campaign — say so, and
                  don't render the tracker / fake booked bar / receipt below. */}
              {isIncomplete && (
                <div className="mt-4">
                  <Banner tone="caution" label="Payment not completed" icon={<CreditCard className="w-3 h-3" />}>
                    <p>
                      This campaign was never submitted, and your card was not charged. Your
                      commercial is saved, so you can pick up where you left off or discard this
                      checkout.
                    </p>
                  </Banner>
                </div>
              )}

              {/* Progress / tracker */}
              {(() => {
                if (!cfg || isRejected || isCanceled || isIncomplete) return null;
                const bucket = bucketFor(row);
                const isActiveOrCompleted = row.status === 'approved' && (bucket === 'active' || bucket === 'completed');
                if (isActiveOrCompleted) {
                  const progress = campaignDelivery(row);
                  const { startDate, endDate } = campaignDates(row);
                  return (
                    <>
                      {progress.hasData && (
                        <div className="mt-5 pt-4 border-t border-slate-100">
                          <AdPlayProgress
                            current={progress.current}
                            total={progress.total!}
                            percent={progress.percent}
                            startDate={startDate}
                            endDate={endDate}
                            prominent
                          />
                        </div>
                      )}
                      <div className="mt-3">
                        <CampaignStatusDashboard config={cfg} currentStep={4} mini completed={bucket === 'completed'} />
                      </div>
                    </>
                  );
                }
                const isScheduled = bucket === 'scheduled';
                // CTV-658: a gated approved campaign (whole Live Set pending
                // review or awaiting a fix) regresses the tracker honestly.
                const gate = approvedReviewGate(row);
                const trackerStep = gate === 'under_review'
                  ? 1
                  : gate === 'needs_fix'
                    ? 0
                    : isScheduled ? 3 : statusToStep(row.status);
                const scheduledStart = isScheduled ? row.campaign?.startDate : null;
                // CTV-516: scheduled / pending campaigns haven't started
                // delivering yet, but the buyer has already booked a fixed
                // number of impressions. Replicate the delivery bar empty
                // (0 of the booked goal) so the goal is visible before the
                // campaign goes live. CTV-640: the goal comes from the
                // server-authoritative pacing_impressions (which includes any
                // Plays boost) via bookedPlays; the budget/CPM recompute is
                // only the pre-flight fallback.
                const booked = bookedPlays(
                  row,
                  cfg.products.find((p) => p.id === row.campaign?.selectedProduct)?.cpm ?? null,
                );
                // CTV-658: a gated campaign that already ran keeps its REAL
                // delivery bar; only never-ran rows fall back to the empty
                // booked-goal bar.
                const delivered = campaignDelivery(row);
                const showDeliveredBar = !!gate && hasDeliveredData(row);
                const showBookedBar =
                  !showDeliveredBar && (isScheduled || bucket === 'pending') && booked != null && booked > 0;
                const { startDate, endDate } = campaignDates(row);
                return (
                  <>
                    {showDeliveredBar && (
                      <div className="mt-5 pt-4 border-t border-slate-100">
                        <AdPlayProgress
                          current={delivered.current}
                          total={delivered.total!}
                          percent={delivered.percent}
                          startDate={startDate}
                          endDate={endDate}
                          prominent
                        />
                      </div>
                    )}
                    {showBookedBar && (
                      <div className="mt-5 pt-4 border-t border-slate-100">
                        <AdPlayProgress
                          current={0}
                          total={booked!}
                          percent={0}
                          startDate={startDate}
                          endDate={endDate}
                          prominent
                        />
                      </div>
                    )}
                    <div className={showBookedBar || showDeliveredBar ? 'mt-3' : 'mt-5 pt-4 border-t border-slate-100'}>
                      {isScheduled && scheduledStart ? (
                        <ScheduledStartBanner startDate={scheduledStart} />
                      ) : (
                        <CampaignStatusDashboard config={cfg} currentStep={trackerStep} compact />
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Rejection — structured */}
              {isRejected && (
                <div className="mt-4">
                  <Banner
                    tone="warn"
                    label={`Not approved by ${publisherName}`}
                    icon={<XCircle className="w-3 h-3" />}
                  >
                    {(row.rejection_reason_codes && row.rejection_reason_codes.length > 0) ? (
                      <ul className="space-y-1">
                        {row.rejection_reason_codes.map((code) => {
                          const r = REJECTION_REASONS_BY_CODE[code];
                          if (!r) return null;
                          return (
                            <li key={code} className="leading-relaxed">
                              <span className="font-medium text-rose-900">{r.label}</span>
                              {r.advertiserText && <> — <span className="text-rose-700/90">{r.advertiserText}</span></>}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p>Check your email for details.</p>
                    )}
                    {row.rejection_reason && (
                      <div className="mt-2 pl-3 border-l-2 border-rose-200 leading-relaxed italic">
                        "{row.rejection_reason}"
                      </div>
                    )}
                    <p className="mt-2">
                      Your card was never charged. You can submit a new ad anytime.
                    </p>
                  </Banner>
                </div>
              )}
            </div>

            {/* CTV-445: performance reporting (delivery, completion rate,
                geographic breakdown, and later daypart + by-creative) lives on
                a dedicated page, linked here for delivering/completed campaigns.
                CTV-658: gated campaigns that already ran keep the link. */}
            {(bucketFor(row) === 'completed' || bucketFor(row) === 'active' ||
              (row.status === 'approved' && hasDeliveredData(row))) && (
              <Link
                to={`/${slug}/my-campaigns/${row.id}/report`}
                className="block rounded-xl p-5 text-white shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all group"
                style={{
                  background: `linear-gradient(135deg, ${REPORT_GRADIENT_FROM}, ${REPORT_GRADIENT_TO})`,
                }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center shrink-0 group-hover:bg-white/25 transition-colors">
                      <BarChart3 className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[15px] font-bold tracking-tight">View performance report</div>
                      <div className="text-[12.5px] text-white/80 truncate">
                        Impressions, completion rate, and where your ad delivered
                      </div>
                    </div>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1.5 bg-white/15 group-hover:bg-white/25 rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors">
                    View report
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            )}

            {/* Status-driven action bar — same buttons that appear on the
                My Campaigns card, hoisted into a prominent strip here. */}
            {cfg && isIncomplete && (
              // CTV-637: a stranded checkout gets finish/discard, not the
              // rerun/extend actions below (which assume a real campaign).
              <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: accent }}>
                    Actions
                  </div>
                  <div className="text-[13px] text-slate-600 mt-0.5">
                    Your card was never charged. Finish paying to submit this campaign, or discard it.
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <FinishCheckoutButton submission={row} cfg={cfg} />
                  <CancelButton
                    submission={row}
                    cfg={cfg}
                    onCanceled={(_id, canceledAt) => {
                      setRow((prev) =>
                        prev ? { ...prev, status: 'canceled', canceled_at: canceledAt } : prev,
                      );
                    }}
                  />
                </div>
              </div>
            )}
            {cfg && !isIncomplete && (() => {
              const bucket = bucketFor(row);
              const onArchived = bucket === 'archived';
              const onActiveOrScheduled = bucket === 'active' || bucket === 'scheduled';
              const hasCreative = !!row.creatives?.[0];
              const showCancel = canCancel(row);
              // CTV-661: the in-modal Edit also returns for an approved
              // campaign whose whole live set regressed to "Under Review"
              // (canEditInModal ⊇ active/scheduled). State 1
              // (payment_authorized) uses EditPreApprovalButton → wizard.
              const showModalEdit = canEditInModal(row);
              const isPreApproval = row.status === 'payment_authorized';
              // CTV-466 follow-up: rejection resolution flows through the
              // wizard (Fix & resubmit) instead of an in-page modal. We
              // surface the action when status === creative_rejected
              // regardless of bucket (rejected now lives in the Pending tab).
              const isRejected = row.status === 'creative_rejected';
              if (!onArchived && !showModalEdit && !hasCreative && !showCancel && !isRejected) return null;
              return (
                <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: accent }}>
                      Actions
                    </div>
                    <div className="text-[13px] text-slate-600 mt-0.5">
                      {isRejected
                        ? 'Replace or remove the rejected commercials, then resubmit for re-review.'
                        : onArchived
                          ? 'Pick up where this campaign left off.'
                          : onActiveOrScheduled
                            ? 'Extend this campaign or start a new one.'
                            : 'Start a new campaign with this same ad.'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(onArchived || isRejected) && hasCreative && (
                      <FixAndResubmitButton
                        submission={row}
                        cfg={cfg}
                      />
                    )}
                    {showModalEdit && (
                      <EditButton
                        submission={row}
                        cfg={cfg}
                        // CTV-661: under_review has no active creatives, so the
                        // Extend tab would only warn, so open on the swap tab.
                        defaultTab={approvedReviewGate(row) === 'under_review' ? 'swap' : undefined}
                        onExtended={handleExtended}
                        onCreativeSwapped={handleCreativeSwapped}
                      />
                    )}
                    {/* CTV-661: a payment_authorized submission gets Edit →
                        wizard (discard-and-recreate) instead of Rerun. */}
                    {isPreApproval && hasCreative && (
                      <EditPreApprovalButton
                        submission={row}
                        cfg={cfg}
                      />
                    )}
                    {!onArchived && hasCreative && !isPreApproval && (
                      <RerunMenu
                        submission={row}
                        cfg={cfg}
                      />
                    )}
                    {showCancel && !onActiveOrScheduled && (
                      <CancelButton
                        submission={row}
                        cfg={cfg}
                        onCanceled={(_id, canceledAt) => {
                          setRow((prev) =>
                            prev ? { ...prev, status: 'canceled', canceled_at: canceledAt } : prev,
                          );
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })()}

            {/* CTV-658 Option A: the whole Live Set was swapped out and is
                awaiting publisher review — nothing can deliver until it's
                approved. Mirrors the My Campaigns card banner; copy carries
                the already-paid fact and the unblock path. */}
            {approvedReviewGate(row) === 'under_review' && (() => {
              const todayStr = new Date().toISOString().slice(0, 10);
              const startsFuture =
                !!row.campaign?.startDate && row.campaign.startDate.slice(0, 10) > todayStr;
              return (
                <Banner tone="caution" label="New commercial under review">
                  You replaced your commercial. <strong>{cfg?.name ?? 'The station'}</strong> is
                  reviewing the new one; delivery{' '}
                  {startsFuture ? "starts once it's approved." : "resumes once it's approved."}
                </Banner>
              );
            })()}

            {/* CTV-487: Paused Delivery surfaces. The Active pause banner
                renders when today is inside a Live-Set Coverage Gap. The
                Upcoming pause note (below in the creative section) renders
                when a pending creative is scheduled to start in the future
                and would create a gap before then. Both derive from the
                active-only Live Set + pending_review creatives (CTV-611).
                CTV-658: suppressed while a review gate is active — the gate
                banners above (or the rejected card below) already carry the
                cause, and "waiting to approve" would be wrong for needs_fix. */}
            {(() => {
              if (row.status !== 'approved') return null;
              if (approvedReviewGate(row)) return null;
              const creatives = row.creatives ?? [];
              const todayStr = (() => {
                const d = new Date();
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${y}-${m}-${day}`;
              })();
              const activeRanges = creatives
                .filter((c) => c.status === 'active')
                .map((c) => ({ startDate: c.creative_start_date, endDate: c.creative_end_date }));
              const cov = validateCreativeCoverage({
                windowStart: row.campaign?.startDate ?? '',
                windowEnd: row.campaign?.endDate ?? '',
                creatives: activeRanges,
              });
              const activePause = cov.uncoveredRanges.find(
                (r) => r.startDate <= todayStr && r.endDate >= todayStr,
              );
              if (!activePause) return null;
              // CTV-611: pendingCreativeSwap is retired; the first
              // pending_review creative is the file awaiting approval.
              const pendingFile =
                creatives.find((c) => c.status === 'pending_review')?.filename ?? null;
              return (
                <Banner tone="info" label="Delivery paused">
                  Delivery paused — waiting for <strong>{cfg?.name ?? 'the station'}</strong> to approve
                  {pendingFile ? <> <strong>{pendingFile}</strong></> : ' your new commercial'}.
                </Banner>
              );
            })()}

            {/* Commercial — CTV-658: one card per creative in every case
                (the legacy single-creative layout is gone — it carried no
                status treatment, so pending/rejected lone creatives rendered
                as healthy). The schedule chart renders from 2 chartable
                creatives up. */}
            <SectionCard title={(row.creatives?.length ?? 0) > 1 ? 'Commercials' : 'Commercial'} accent={accent}>
              {(() => {
                // CTV-457: positional palette anchored on the station's
                // primary + secondary so the chart reads as the same brand
                // family as the wizard surface.
                const palette = buildStationPalette(
                  cfg?.primaryColor,
                  cfg?.secondaryColor,
                  Math.max(row.creatives!.length, 6),
                );
                const colors = row.creatives!.map((_, i) => palette[i % palette.length]);
                // CTV-487: upcoming-pause info note. Surfaces when a pending
                // creative is scheduled to start in the future AND the active
                // Live Set would have a gap before then (so when today crosses
                // the gap's start without the pending getting approved, the
                // campaign pauses).
                const upcoming = (() => {
                  if (row.status !== 'approved') return null;
                  const todayStr = (() => {
                    const d = new Date();
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${y}-${m}-${day}`;
                  })();
                  const pending = (row.creatives ?? []).find(
                    (c) => c.status === 'pending_review' && c.creative_start_date > todayStr,
                  );
                  if (!pending) return null;
                  return { filename: pending.filename, startDate: pending.creative_start_date };
                })();
                return (
                <div className="px-5 py-4 space-y-5">
                  {upcoming && (
                    <div className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                      <strong>{upcoming.filename}</strong> is awaiting review. Scheduled to start <strong>{upcoming.startDate}</strong>.
                    </div>
                  )}
                  {/* CTV-658: chart from 2 chartable creatives up — a lone
                      bar restates the card header's dates, and voided rows
                      never chart at all. */}
                  {chartCreatives(row.creatives).length > 1 && (
                  <CreativeScheduleChart
                    readOnly
                    accentColor={accent ?? '#3b82f6'}
                    colors={colors}
                    slug={cfg?.slug}
                    campaignStartDate={row.campaign?.startDate ?? ''}
                    campaignEndDate={row.campaign?.endDate ?? ''}
                    slots={chartCreatives(row.creatives).map((c) => ({
                      slotId: c.id,
                      filename: c.filename,
                      startDate: c.creative_start_date,
                      endDate: c.creative_end_date,
                      storagePath: c.storage_path,
                      // CTV-606 (ported in CTV-611): status drives per-bar
                      // visual treatment: retired renders faded, pending
                      // shows an "Awaiting review" chip.
                      status:
                        c.status === 'retired' || c.status === 'pending_review'
                          ? c.status
                          : ('active' as const),
                    }))}
                    segments={computeRotationSegments(
                      chartCreatives(row.creatives).map((c) => ({
                        id: c.id,
                        startDate: c.creative_start_date,
                        endDate: c.creative_end_date,
                        segmentShares: (c.segment_shares ?? []).map((s) => ({
                          startDate: s.start_date,
                          endDate: s.end_date,
                          share: s.share,
                        })),
                      })),
                      row.campaign?.startDate ?? '',
                      row.campaign?.endDate ?? '',
                    )}
                  />
                  )}
                  {/* CTV-611 / ADR-0023 display rule: cards render exactly
                      four buckets: active, pending_review, retired, and
                      voided ONLY while unresolved (resolved_at null). Resolved
                      voided rows (a handled rejection is not delivery
                      history), withdrawn, and abandoned rows are hidden. The
                      Gantt input above is deliberately unchanged. */}
                  {(() => {
                    const cardRows = row.creatives!.filter(
                      (c) =>
                        c.status === 'active' ||
                        c.status === 'pending_review' ||
                        c.status === 'retired' ||
                        (c.status === 'voided' && c.resolved_at == null),
                    );
                    // CTV-611: completed runs (retired, or active whose
                    // window already ended) collapse behind an expander so
                    // the default view is only what's live, pending, or
                    // needs fixing. Collapsed cards are not mounted, so
                    // their signed video URLs aren't fetched until opened.
                    const isPastRun = (c: SelfServiceCreativeRow) =>
                      c.status === 'retired' ||
                      (c.status === 'active' && c.creative_end_date < localTodayYmd());
                    const currentRows = cardRows.filter((c) => !isPastRun(c));
                    const pastRows = cardRows.filter(isPastRun);
                    const pastOpen = showPastCreatives || currentRows.length === 0;
                    const renderCard = (c: SelfServiceCreativeRow) => (
                      <MultiCreativeCard
                        key={c.id}
                        submissionId={row.id}
                        creative={c}
                        accent={accent}
                        primaryColor={cfg?.primaryColor}
                        onFixAndResubmit={
                          row.status === 'approved' &&
                          c.status === 'voided' &&
                          c.resolved_at == null
                            ? () => {
                                // Parity with EditButton's open tracking.
                                analytics.track(EVENTS.EDIT_CAMPAIGN_OPENED, {
                                  station_slug: slug,
                                  submission_id: row.id,
                                  tab: 'swap',
                                });
                                setFixModalOpen(true);
                              }
                            : undefined
                        }
                      />
                    );
                    return (
                      <>
                        {currentRows.map(renderCard)}
                        {pastRows.length > 0 && (
                          <div className="pt-1">
                            <button
                              type="button"
                              onClick={() => setShowPastCreatives((v) => !v)}
                              className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
                              aria-expanded={pastOpen}
                            >
                              <ChevronDown
                                className={`w-3.5 h-3.5 transition-transform ${pastOpen ? 'rotate-180' : ''}`}
                              />
                              Ran previously ({pastRows.length})
                            </button>
                            {pastOpen && (
                              <div className="mt-3 space-y-4">{pastRows.map(renderCard)}</div>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                );
              })()}
            </SectionCard>

            {/* Business */}
            <SectionCard title="Business" accent={accent}>
              <DetailRow icon={Building2} label="Business" value={row.business_info?.businessName ?? '—'} accent={accent} />
              {row.business_info?.businessWebsite && (
                <DetailRow icon={Globe} label="Website" value={row.business_info.businessWebsite} accent={accent} />
              )}
              <DetailRow icon={Mail} label="Email" value={row.contact_email} accent={accent} />
              {row.business_info?.contactName && (
                <DetailRow icon={User} label="Contact" value={row.business_info.contactName} accent={accent} />
              )}
              {row.business_info?.contactPhone && (
                <DetailRow icon={Phone} label="Phone" value={row.business_info.contactPhone} accent={accent} />
              )}
            </SectionCard>

            {/* Payment — CTV-516: a single Order Receipt is the source of
                truth for all payment information. The old standalone Charges
                and Status rows (which restated the receipt's totals) are gone;
                anything actionable — the declined-payment recovery flows —
                still sits above the receipt. Extensions feed the receipt's
                upfront payment rows; the receipt itself lists initial + each
                extension + the total, with actual dates and statuses. */}
            {/* CTV-637: no receipt for a checkout that never charged. Rendering
                OrderReceipt here would print a "Total Authorized" header, a
                receipt number, and a Download-PDF button for $0 of real money. */}
            {cfg && !isIncomplete && (
              <div>
                <div className="flex items-center justify-between mb-2 px-1">
                  <div
                    className="text-[10px] font-semibold uppercase tracking-[0.12em]"
                    style={{ color: accent || 'rgb(100, 116, 139)' }}
                  >
                    Payment
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadReceipt}
                    disabled={downloading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {downloading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    {downloading ? 'Generating…' : 'Download PDF'}
                  </button>
                </div>
                {downloadError && (
                  <div className="mb-2">
                    <Banner tone="warn" label="Couldn't generate PDF">{downloadError}</Banner>
                  </div>
                )}

                {/* Declined-payment recovery — actionable, so it stays above
                    the receipt rather than living as a passive status row. */}
                {row.monthlyPaymentRecovery ? (
                  <div className="rounded-2xl border border-rose-100 bg-rose-50/80 overflow-hidden mb-3 px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                        <AlertCircle className="w-4 h-4 text-rose-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-rose-950 tracking-tight">
                          A monthly payment was declined
                        </div>
                        <p className="text-[12px] text-rose-800/90 mt-1">
                          Update the card by {formatDateTime(row.monthlyPaymentRecovery.nextAttemptAt)} to pay the missed month and keep future monthly charges running.
                        </p>
                      </div>
                    </div>
                    <Link
                      to={`/${slug}/my-campaigns/${row.id}/update-payment`}
                      className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold text-white"
                      style={{ backgroundColor: accent || cfg.primaryColor }}
                    >
                      Update card and retry payment
                    </Link>
                  </div>
                ) : row.status === 'payment_failed' && row.payment_plan === 'upfront' ? (
                  <div className="rounded-2xl border border-rose-100 bg-rose-50/80 overflow-hidden mb-3 px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                        <AlertCircle className="w-4 h-4 text-rose-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-rose-950 tracking-tight">
                          We couldn't complete this payment
                        </div>
                        <p className="text-[12px] text-rose-800/90 mt-1">
                          Update the card on file and retry the charge. If your bank asks for verification, you can complete it here.
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 space-y-3">
                      {!recoveryClientSecret && !recoveryAction && (
                        <button
                          type="button"
                          disabled={recoveryBusy}
                          onClick={startPaymentRecovery}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
                          style={{ backgroundColor: accent || cfg.primaryColor }}
                        >
                          {recoveryBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                          Update card and retry payment
                        </button>
                      )}

                      {recoveryClientSecret && recoverySetupIntentId && !recoveryAction && (
                        <StripePaymentSection
                          clientSecret={recoveryClientSecret}
                          intentType="setup"
                          accentColor={accent || cfg.primaryColor}
                          amountCents={row.amount_cents}
                          submitLabel={recoveryBusy ? 'Retrying payment...' : 'Save card and retry payment'}
                          disabled={recoveryBusy}
                          onSuccess={() => retryPaymentRecovery()}
                          onError={(msg) => setRecoveryError(msg)}
                        />
                      )}

                      {recoveryAction && (
                        <div className="space-y-2">
                          <p className="text-[12px] text-rose-800/90">
                            Your bank needs one more verification step before we can recover the payment.
                          </p>
                          <StripePaymentActionSection
                            clientSecret={recoveryAction.clientSecret}
                            accentColor={accent || cfg.primaryColor}
                            onSuccess={(id) => retryPaymentRecovery(id ?? recoveryAction.paymentIntentId)}
                            onError={(msg) => setRecoveryError(msg)}
                          />
                        </div>
                      )}

                      {recoveryError && (
                        <div className="space-y-2">
                          <p className="text-[12px] text-rose-700 bg-white/70 border border-rose-200 rounded-lg px-3 py-2">
                            {recoveryError}
                          </p>
                          {(recoveryClientSecret || recoveryAction) && (
                            <button
                              type="button"
                              className="text-[12px] font-semibold text-rose-700 hover:text-rose-900"
                              onClick={() => {
                                setRecoveryClientSecret(null);
                                setRecoverySetupIntentId(null);
                                setRecoveryAction(null);
                                setRecoveryError(null);
                              }}
                            >
                              Try a different card
                            </button>
                          )}
                        </div>
                      )}

                      {recoverySuccess && (
                        <Banner tone="success" label="Updated">{recoverySuccess}</Banner>
                      )}
                    </div>
                  </div>
                ) : row.status === 'payment_failed' ? (
                  <div className="rounded-2xl border border-rose-100 bg-rose-50/80 overflow-hidden mb-3 px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                        <AlertCircle className="w-4 h-4 text-rose-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-rose-950 tracking-tight">
                          We couldn't complete this payment
                        </div>
                        <p className="text-[12px] text-rose-800/90 mt-1">
                          This campaign does not currently have a self-service payment update window. Contact the publisher if you need help with this payment.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* The Order Receipt — the single, canonical, redownloadable
                    money summary. Includes the initial payment, every
                    extension, the schedule, dates, and statuses. */}
                <OrderReceipt row={row} config={cfg} extensionCharges={extensionCharges} />
              </div>
            )}

            {/* Campaign edits — CTV-516: an edit history (NOT payment history),
                separate from Payment. Lists extensions (date + amount changes)
                and creative changes (old → new creatives, date changed). */}
            <CampaignEditsCard edits={edits} accent={accent} />

            <div className="text-[11px] text-slate-400 px-1">
              Submitted {formatDateTime(row.created_at)}
              {row.reviewed_at && ` · Reviewed ${formatDateTime(row.reviewed_at)}`}
              {row.canceled_at && ` · Canceled ${formatDateTime(row.canceled_at)}`}
            </div>

            {/* CTV-611: page-level Edit-campaign modal, opened by the
                per-creative Fix & resubmit CTA preselected on the Edit
                commercial tab (the recovery block lives there). */}
            {cfg && fixModalOpen && (
              <EditCampaignModal
                submission={row}
                cfg={cfg}
                defaultTab="swap"
                onClose={() => setFixModalOpen(false)}
                onExtended={handleExtended}
                onCreativeSwapped={handleCreativeSwapped}
              />
            )}

            {/* Markers / actions could go here later (Extend / Run again) */}
          </div>
        )}
      </main>
    </div>
  );
};

// CTV-516: edit-history card (extensions + creative changes). This is an
// audit trail, NOT payment history — payment lives entirely in the receipt
// above. Extensions show the end-date and budget change; creative changes show
// the old creative(s), the new creative(s), and the date changed.
const CampaignEditsCard: React.FC<{ edits: EditRow[]; accent?: string }> = ({ edits, accent }) => {
  // Hide voided edits (reverted/abandoned, never took effect) and failed edits
  // (Publica push-job errors are an internal backend concern — advertisers
  // shouldn't see infrastructure flakes; publisher/admin views still see them).
  // Show the most recent change first.
  const visible = edits
    .filter((e) => e.status !== 'voided' && e.status !== 'failed')
    .filter((e) => e.edit_kind === 'extend' || e.creative_change != null);
  if (visible.length === 0) return null;
  const ordered = [...visible].reverse();

  const statusNote = (e: EditRow): string | null => {
    switch (e.status) {
      case 'pending_review':
        return 'Pending review';
      case 'ready':
        return 'Approved — applying';
      default:
        return null; // applied
    }
  };

  return (
    <SectionCard title="Campaign edits" accent={accent} id="creative-edits">
      {ordered.map((e) => {
        const when = e.applied_at ?? e.created_at;
        const note = statusNote(e);
        const isExtend = e.edit_kind === 'extend';
        return (
          <div key={e.id} className="flex items-start gap-3 px-5 py-3">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
              style={{ backgroundColor: `${accent || '#94a3b8'}14`, color: accent || 'rgb(100, 116, 139)' }}
            >
              {isExtend ? <Calendar className="w-3.5 h-3.5" /> : <Film className="w-3.5 h-3.5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[13px] text-slate-900 font-medium tracking-tight">
                  {isExtend ? 'Campaign extended' : 'Creative updated'}
                </div>
                <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400 shrink-0">
                  {formatDate(when)}
                </div>
              </div>

              {isExtend ? (
                <div className="text-[12px] text-slate-600 mt-0.5 space-y-0.5">
                  {e.previous_end_date && e.new_end_date && (
                    <div>
                      End date {formatDate(e.previous_end_date)}{' '}
                      <ArrowRight className="inline w-3 h-3 text-slate-400 -mt-0.5" />{' '}
                      {formatDate(e.new_end_date)}
                    </div>
                  )}
                  {e.additional_budget_cents != null && e.additional_budget_cents > 0 && (
                    <div>Added ${formatMoneySmart(e.additional_budget_cents / 100)} in budget</div>
                  )}
                </div>
              ) : (
                <div className="text-[12px] text-slate-600 mt-0.5 space-y-1">
                  {(e.creative_change?.removed.length ?? 0) > 0 && (
                    <div>
                      <span className="text-slate-400">Previous: </span>
                      {e.creative_change!.removed.map((c) => c.filename).join(', ')}
                    </div>
                  )}
                  {(e.creative_change?.added.length ?? 0) > 0 && (
                    <div>
                      <span className="text-slate-400">New: </span>
                      {e.creative_change!.added.map((c) => c.filename).join(', ')}
                    </div>
                  )}
                  {(e.creative_change?.updated.length ?? 0) > 0 && (
                    <div>
                      <span className="text-slate-400">Rescheduled: </span>
                      {e.creative_change!.updated
                        .map((c) =>
                          c.startDate && c.endDate
                            ? `${c.filename} (${formatDate(c.startDate)} – ${formatDate(c.endDate)})`
                            : c.filename,
                        )
                        .join(', ')}
                    </div>
                  )}
                </div>
              )}

              {note && (
                <div className="text-[10px] font-semibold uppercase tracking-[0.08em] mt-1 text-amber-700">
                  {note}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </SectionCard>
  );
};

const SectionCard: React.FC<{ title: string; children: React.ReactNode; accent?: string; id?: string }> = ({ title, children, accent, id }) => (
  <div id={id}>
    <div
      className="text-[10px] font-semibold uppercase tracking-[0.12em] mb-2 px-1"
      style={{ color: accent || 'rgb(100, 116, 139)' }}
    >
      {title}
    </div>
    <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden divide-y divide-slate-200/70">
      {children}
    </div>
  </div>
);

interface DetailRowProps {
  icon?: React.FC<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: string;
  accent?: string;
}

const DetailRow: React.FC<DetailRowProps> = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-3 px-5 py-3">
    {Icon && (
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${accent || '#94a3b8'}14`, color: accent || 'rgb(100, 116, 139)' }}
      >
        <Icon className="w-3.5 h-3.5" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className="text-[13px] text-slate-900 font-medium tracking-tight truncate">{value || '—'}</div>
    </div>
  </div>
);

// CTV-457: per-creative card on the advertiser detail page. Each card pulls
// its own short-lived signed URL via /creatives/view-url with the explicit
// creativeId so the multi-creative case shows the right file in each player.
const MultiCreativeCard: React.FC<{
  submissionId: string;
  creative: NonNullable<SelfServiceSubmissionRow['creatives']>[number];
  accent?: string;
  // CTV-611: station primary colour for the Fix & resubmit gradient pill.
  primaryColor?: string;
  // CTV-611: present only on unresolved voided cards of approved campaigns;
  // opens the Edit-campaign modal on the Edit commercial tab.
  onFixAndResubmit?: () => void;
}> = ({ submissionId, creative, accent, primaryColor, onFixAndResubmit }) => {
  const isVoided = creative.status === 'voided';
  // CTV-606 (ported in CTV-611): retired and pending_review get their own
  // header pills so buyers reading the card list can tell delivery history
  // ("Ran") and in-review rows ("Awaiting review") from live ones.
  const isRetired = creative.status === 'retired';
  const isPending = creative.status === 'pending_review';
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(null);
    apiClient
      .post<{ url: string }>('/creatives/view-url', {
        submissionId,
        creativeId: creative.id,
      })
      .then(({ url: signedUrl }) => { if (!cancelled) setUrl(signedUrl); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Preview unavailable'); });
    return () => { cancelled = true; };
  }, [submissionId, creative.id]);

  return (
    <div className={`rounded-xl border bg-white overflow-hidden ${isVoided ? 'border-rose-300' : 'border-slate-200'}`}>
      <div className={`flex items-center gap-2 px-4 py-2 border-b ${isVoided ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
        <span className={`text-[11px] font-semibold tracking-tight truncate flex-1 ${isVoided ? 'text-rose-900' : 'text-slate-800'}`}>
          {creative.filename}
        </span>
        {isVoided ? (
          <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-full text-rose-700 bg-rose-100">
            Rejected
          </span>
        ) : isPending ? (
          // CTV-606 (G8): pending creatives are in publisher review; the
          // segment-share summary is meaningless until they land active.
          <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-full text-amber-800 bg-amber-100">
            Awaiting review
          </span>
        ) : isRetired ? (
          // CTV-606 (G7): retired creatives are historical. Distinct pill
          // from active so buyers reading the card list understand the
          // row is delivery history, not currently live.
          <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-full text-slate-500 bg-slate-100">
            Ran
          </span>
        ) : (
          <span
            className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-full"
            style={{ color: accent, backgroundColor: `${accent || '#94a3b8'}14` }}
          >
            {formatSegmentSharesSummary({
              id: creative.id,
              startDate: creative.creative_start_date,
              endDate: creative.creative_end_date,
              segmentShares: (creative.segment_shares ?? []).map((s) => ({
                startDate: s.start_date,
                endDate: s.end_date,
                share: s.share,
              })),
            }) || '—'}
          </span>
        )}
      </div>
      {isVoided && (creative.rejection_reason || (creative.rejection_reason_codes && creative.rejection_reason_codes.length > 0)) && (
        <div className="px-4 py-3 bg-rose-50/60 border-b border-rose-200 text-[12px]">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-rose-700 mb-1">Reviewer notes</div>
          {creative.rejection_reason_codes && creative.rejection_reason_codes.length > 0 && (
            <ul className="space-y-0.5 mb-1">
              {creative.rejection_reason_codes.map((code) => {
                const r = REJECTION_REASONS_BY_CODE[code];
                if (!r) return null;
                return (
                  <li key={code} className="text-rose-800">
                    <span className="font-medium text-rose-900">{r.label}</span>
                    {r.advertiserText && <> — <span className="text-rose-700/90">{r.advertiserText}</span></>}
                  </li>
                );
              })}
            </ul>
          )}
          {creative.rejection_reason && (
            <div className="pl-3 border-l-2 border-rose-200 text-rose-800 leading-relaxed italic">
              "{creative.rejection_reason}"
            </div>
          )}
        </div>
      )}
      {/* CTV-611: recovery CTA on unresolved rejections. Mirrors the
          station-gradient pill of FixAndResubmitButton; the shared
          buyer-facing label covers both recovery operations. */}
      {isVoided && onFixAndResubmit && (
        <div className="px-4 py-2.5 bg-rose-50/60 border-b border-rose-200 flex items-center justify-between gap-3">
          <span className="text-[12px] text-rose-800">
            Send a fixed commercial for re-review.
          </span>
          <button
            type="button"
            onClick={onFixAndResubmit}
            className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full text-white transition-all hover:brightness-110 active:scale-[0.98]"
            style={{
              background: `linear-gradient(180deg, ${accent}, ${primaryColor ?? accent})`,
              boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${primaryColor ?? accent}`,
            }}
          >
            Fix & resubmit
            <span aria-hidden>→</span>
          </button>
        </div>
      )}
      <div
        className="relative aspect-video bg-black"
        style={{
          boxShadow: `0 12px 32px -18px rgba(15,23,42,0.25), 0 4px 12px -8px ${(accent || '#94a3b8')}30`,
        }}
      >
        {url ? (
          <video src={url} controls preload="none" className="w-full h-full object-contain bg-black" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-white/80 bg-gradient-to-br from-slate-900 to-slate-700">
            {error ? (
              <>
                <Film className="w-6 h-6 text-white/40 mb-2" />
                <div className="text-[12px] font-medium">Preview unavailable</div>
                <div className="text-[10px] text-white/50 mt-1 max-w-xs">{error}</div>
              </>
            ) : (
              <>
                <Loader2 className="w-5 h-5 text-white/60 animate-spin" />
                <div className="text-[11px] text-white/60 mt-2">Loading preview…</div>
              </>
            )}
          </div>
        )}
      </div>
      <div className="px-4 py-2 text-[11px] text-slate-600 border-t border-slate-200 flex items-center gap-2 flex-wrap">
        {/* CTV-611: tightened from CTV-606's future-start-only rule: pending_review ALWAYS reads "Scheduled" (an unapproved creative cannot be running, even with a today/past start date). */}
        <span className="font-medium">
          {isRetired ? 'Ran' : isPending ? 'Scheduled' : 'Runs'}
        </span>
        <span>{creative.creative_start_date} → {creative.creative_end_date}</span>
        {creative.click_through_url && (
          <>
            <span className="text-slate-300">·</span>
            <ExternalLink className="w-3 h-3 text-slate-400" />
            <span className="truncate">{creative.click_through_url}</span>
          </>
        )}
      </div>
    </div>
  );
};

// Tv/MapPin/Calendar imports kept for potential future use — silence
// unused-import lint by referencing them once.
void Tv; void MapPin; void Calendar; void Radio;

export default AdvertiserCampaignDetailPage;
